import { useMutation } from '@apollo/client'
import { Box, Code, Divider, FormModal, Link, Text, VStack } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { CopyableText } from '../../../_shared/components'
import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_TAI_TMS_APP } from '../data/mutations'

/**
 * Generate's an api key for Tai to use in addition to input an api key from Tai.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 * @param props.isModalEditable determines if the modal is in edit mode
 * @param props.setModalEditable function to toggle edit mode
 */
const TaiTMSAppModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, app, refetchApps, isModalEditable, setModalEditable } = props
  const [apiKey, setAPIKey] = useState('')
  const [formError, setFormError] = useState('')

  const { showToast } = Toast()

  const [enableTaiTMSApp, { loading: enableAppLoading }] = useMutation(ENABLE_TAI_TMS_APP, {
    onCompleted: (result) => {
      setAPIKey(result.enableTaiTMSApp.value)
      showToast({
        status: 'success',
        title: isModalEditable ? 'Tai TMS app updated successfully!' : 'Tai TMS app enabled successfully!'
      })
      refetchApps()
      if (isModalEditable && setModalEditable) {
        setModalEditable(false)
      }
    },
    onError: (error) => {
      showToast({ status: 'error', title: error.message })
      setFormError(error.message)
    }
  })

  // Display both the user-provided Tai API key and Chain's generated API token
  const footer = (
    <Box>
      <VStack spacing={4} width="100%" align="stretch">
        {formError && (
          <Box mb={4}>
            <Text color="red.600">{formError}</Text>
          </Box>
        )}

        <Divider />

        {/* Show Chain-generated API token */}
        {(apiKey || app?.workspaceApp?.appCredentials?.chaineAPIToken) && (
          <Box>
            <VStack align="left" width="100%">
              <Text>Chain API Token (for Tai to use):</Text>
              <Code width="100%" p={2} overflowX="auto">
                <CopyableText value={apiKey || app?.workspaceApp?.appCredentials?.chaineAPIToken || ''}>
                  <code className="font-mono text-sm" style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
                    {apiKey || app?.workspaceApp?.appCredentials?.chaineAPIToken}
                  </code>
                </CopyableText>
              </Code>
            </VStack>
          </Box>
        )}

        <Divider />

        <Text>
          {`See `}
          <Link color="blue.600" href={app.docs} target="_blank" rel="noopener noreferrer">
            detailed steps here.
          </Link>
        </Text>
      </VStack>
    </Box>
  )

  type FieldValues = { apiKey?: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    const { apiKey: formAPIKeyValue } = event
    enableTaiTMSApp({
      variables: { apiKey: formAPIKeyValue, appType: appType }
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
    if (isModalEditable && setModalEditable) {
      setModalEditable(false)
    }
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`${isModalEditable ? 'Update' : 'Enable'} ${appType} for Chain`}
        subtitle={`${isModalEditable ? 'Update your' : 'Enabling this integration requires an'} Tai Public API Key.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={isModalEditable ? 'Update' : 'Enable'}
        initialValues={{
          apiKey:
            isModalEditable && app?.workspaceApp?.appCredentials ? app?.workspaceApp?.appCredentials?.apiKey || '' : ''
        }}
        validations={validations}
        isLoading={enableAppLoading}
        footer={footer}
        modalItems={[
          {
            isRequired: true,
            labelText: 'Tai Public API Key',
            name: 'apiKey',
            placeHolder: 'Enter your Tai Public API Key',
            type: 'input'
          }
        ]}
      >
        <Link
          color="blue.500"
          href="https://help.trychain.com/en/collections/11885015-tai-tms"
          target="_blank"
          rel="noopener noreferrer"
        >
          Read the integration guide
        </Link>
      </FormModal>
    </>
  )
})

TaiTMSAppModal.displayName = 'TaiTMSApp'

export { TaiTMSAppModal }
